<template>
  <template v-if="$page">
    <div v-if="currentCity" class="header" :style="{ backgroundColor: currentCity.bg_color }">
      <div class="header__content" :style="{ backgroundColor: currentCity.bg_color }">
        <div class="header__section">
          <h1 class="header__title">
            <HeaderTitle :color="currentCity.accent_color" v-reveal />
          </h1>

          <p class="header__subtitle" v-reveal="{ delay: 200 }">
            {{ currentCity.home.header.subtitle }}
          </p>

          <div class="header__row" v-reveal="{ delay: 225 }">
            <router-link
              :to="{ name: 'region', params: { region: currentCity.name }, hash: '#form' }"
              class="button"
              :style="{ backgroundColor: currentCity.accent_color }"
            >
              {{ $page.header.inscription_btn }}
            </router-link>
            <router-link
              :to="{ name: 'region', params: { region: currentCity.name } }"
              class="button -transparent"
              ><span class="header__button-label">{{ $page.header.details_btn }}</span>
              <Icon id="arrow" />
            </router-link>
          </div>
        </div>

        <div class="header__section -no-flex">
          <div class="header__image">
            <transition name="fade-fast" duration="150" mode="out-in">
              <img
                :src="currentCity.home.header.image.original"
                alt="illustration"
                :key="city"
                v-reveal
              />
            </transition>
          </div>

          <div class="header__regions" v-reveal>
            <div
              v-for="(item, key) in $beet.cities"
              :key="key"
              class="header__region"
              :class="{ '-active': item.id === city }"
              @click="handleRegionClick(item.id)"
            >
              <div class="header__region-name">{{ item.name }}</div>
              <div class="header__region-date">
                {{ item.date ? getLocalDate(item.date) : 'À venir' }}
              </div>
              <div class="header__region-by" v-if="item.id === city && by">{{ by }}</div>
              <div class="header__region-line" v-if="item.id === city">
                <div
                  class="header__region-line-progress"
                  :style="{ animationDuration: `${SLIDE_DURATION}ms` }"
                />
              </div>
            </div>
          </div>

          <div class="header__regions--mobile">
            <div class="header__region -active">
              <div class="header__region-name">{{ currentCity.name }}</div>
              <div class="header__region-date">{{ getLocalDate(currentCity.date) }}</div>
              <div class="header__region-by">{{ by }}</div>

              <div class="header__regions-dots">
                <div
                  v-for="(item, key) in $beet.cities"
                  :key="key"
                  class="header__regions-dot"
                  :class="{ '-active': item.id === city }"
                  @click="handleRegionClick(item.id)"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="header__image--mobile">
          <transition name="fade-fast" duration="150" mode="out-in">
            <img :src="currentCity.home.header.image.original" alt="illustration" :key="city" />
          </transition>
        </div>
      </div>
      <SiteNav
        :sticky="false"
        :style="{ backgroundColor: currentCity.bg_color }"
        @burger="$emit('burger')"
      />
      <div class="header__mouse">
        <Icon id="mouse" />
      </div>
    </div>

    <div class="content">
      <SectionImage v-bind="$page.duo" :image="$page.duo.image.original" hydro />

      <SectionQuote
        v-bind="$page.quote"
        :images="$page.slider.images.map(({ original }) => original)"
      />

      <SectionPartners v-bind="$page.partner" />

      <section class="section section-video">
        <div class="section-video__video">
          <transition name="fade" mode="out-in">
            <div v-if="video" class="section-video__video">
              <iframe
                v-if="video"
                :src="`https://www.youtube.com/embed/${$page.flow.video_id}?autoplay=1`"
                frameborder="0"
                allowfullscreen
                allow="autoplay"
                class="section-video__video"
              />
            </div>

            <div
              v-else
              class="section-video__video"
              :style="{
                backgroundImage: `url(https://i3.ytimg.com/vi/${$page.flow.video_id}/hqdefault.jpg)`,
              }"
              @click="video = true"
              v-reveal
            >
              <button class="section-video__play">
                <Icon id="play-btn" />
              </button>
            </div>
          </transition>
          <img
            class="section-video__video-illustration"
            src="/images/LFLS_VR-Accent.svg"
            alt="illustration"
            width="288"
            height="332"
            v-reveal="{ delay: 300 }"
          />
        </div>

        <div class="section-video__label" v-reveal>
          {{ $page.flow.text.toUpperCase() }}
          <Icon class="section-video__label-arrow" id="arrow-details" />
          <Icon class="section-video__label-arrow--sm" id="arrow-details-sm" />
        </div>

        <h2 class="section__title -sm" v-reveal>{{ $page.activity.title.toUpperCase() }}</h2>
        <p class="section__text" v-reveal="{ delay: 200 }">{{ $page.activity.subtitle }}</p>
        <div v-if="$page.activity.gallery" v-reveal="{ delay: 300 }">
          <LangRouterLink :to="{ name: 'gallery' }" class="button -gallery -black">
            {{ $page.activity.gallery }}
          </LangRouterLink>
        </div>
      </section>

      <SectionImage
        reverse
        v-if="$page.ambassador.content"
        v-bind="$page.ambassador"
        :image="$page.ambassador.image?.original"
      />

      <SectionNews :content="$page.news" v-if="$page.news.length" />

      <SectionComite
        :text="$page.comity.subtitle"
        :title="$page.comity.title"
        :groups="$page.comity.groups"
      />
    </div>
  </template>

  <template v-else>
    <div style="min-height: 100vh" />
  </template>
</template>

<script>
import LangRouterLink from '@/components/global/LangRouterLink.vue'
import HeaderTitle from '@/components/HeaderTitle.vue'
import SectionComite from '@/components/SectionComite.vue'
import SectionImage from '@/components/SectionImage.vue'
import SectionNews from '@/components/SectionNews.vue'
import SectionPartners from '@/components/SectionPartners.vue'
import SectionQuote from '@/components/SectionQuote.vue'
import SiteNav from '@/components/SiteNav.vue'
import seo from '@/helpers/mixins/seo.js'

const SLIDE_DURATION = 5_000

export default {
  name: 'Home',
  beetPage: 'home',
  mixins: [seo],
  emits: ['burger'],
  inject: ['mq'],
  components: {
    LangRouterLink,
    SectionNews,
    HeaderTitle,
    SiteNav,
    SectionComite,
    SectionPartners,
    SectionQuote,
    SectionImage,
  },
  setup() {
    return {
      SLIDE_DURATION,
    }
  },
  computed: {
    by() {
      return this.$beet.partners[this.currentCity.presenter_id]?.name || ''
    },
    currentCity() {
      return this.$beet.cities[this.city] ?? null
    },
  },
  watch: {
    $beet: {
      handler() {
        if (this.$beet.cities) {
          this.city = Object.values(this.$beet.cities)[0].id
          if (!this.timeout) {
            this.timeout = setTimeout(this.goNext, SLIDE_DURATION)
          }
        }
      },
      immediate: true,
    },
  },
  data() {
    return {
      open: true,
      city: null,
      timeout: null,
      video: false,
    }
  },
  methods: {
    goNext(city = null) {
      if (!city) {
        const cities = Object.values(this.$beet.cities)
        const current = cities.findIndex(({ id }) => id === this.city)
        city = current + 1 < cities.length ? cities[current + 1].id : cities[0].id
      }
      this.city = city

      this.timeout = setTimeout(this.goNext, SLIDE_DURATION)
    },
    getLocalDate(date) {
      const d = new Date(date)
      return d.toLocaleDateString('fr', {
        day: 'numeric',
        month: 'long',
      })
    },
    handleRegionClick(cityId) {
      if (this.timeout) clearTimeout(this.timeout)

      this.goNext(cityId)
    },
  },
}
</script>
